import { PauseStep, Step } from './step';
class PatternTransformParams {
    constructor(flipHorizontal = false, flipVertically = false) {
        this.phasing = 0;
        this.scratchSpeed = 1;
        this.flipHorizontal = flipHorizontal;
        this.flipVertically = flipVertically;
    }
    get hasChanges() {
        return (this.phasing !== 0
            || this.scratchSpeed !== 1
            || this.flipHorizontal
            || this.flipVertically);
    }
}
class PatternTransformer {
    transform(_pattern, params) {
        let pattern = _pattern.clone();
        pattern = this.phase(pattern, params);
        pattern = this.flip(pattern, params);
        pattern = this.speed(pattern, params);
        pattern = this.sort(pattern, params);
        pattern = this.addPauses(pattern, params);
        return pattern;
    }
    sort(pattern, params) {
        const compareSteps = (a, b) => {
            if (a.startX < b.startX) {
                return -1;
            }
            if (a.startX > b.startX) {
                return 1;
            }
            return 0;
        };
        pattern.steps.sort(compareSteps);
        return pattern;
    }
    addPauses(pattern, params) {
        let stepsNew = [];
        let lastStep;
        pattern.steps.map((step, index, steps) => {
            // needs pause on begin?
            if (!lastStep && step.startX !== 0) {
                const pauseStepLength = step.startX;
                stepsNew.push(this.createPauseStep(0, pauseStepLength, 0, step.startY));
            }
            // do we need to insert a pause in mid?
            if (lastStep && step.startX !== lastStep.endX) {
                const pauseStepLength = step.startX - lastStep.endX;
                stepsNew.push(this.createPauseStep(lastStep.endX, pauseStepLength, lastStep.endY, step.startY));
            }
            stepsNew.push(step);
            lastStep = step;
            if (index === steps.length - 1) {
                // need to add pause on end to make exact bar?
                const xLast = step.endX;
                let pauseStepLength = 4 - xLast % 4; // @TODO: assumes 4/4 bar
                if (pauseStepLength >= 4) {
                    pauseStepLength = 0;
                }
                if (pauseStepLength > 0) {
                    stepsNew.push(this.createPauseStep(xLast, pauseStepLength, step.endY, 0));
                }
            }
        });
        pattern.steps = stepsNew;
        return pattern;
    }
    createPauseStep(startX, stepLength, startY, endY) {
        const endX = startX + stepLength;
        const xDimensions = [startX, endX];
        const yDimensions = [startY, endY];
        return new PauseStep(xDimensions, yDimensions, false, false);
    }
    flip(pattern, params) {
        pattern.steps.map((step) => {
            if (params.flipHorizontal) {
                step.xDimensions = [
                    4 - step.endX,
                    4 - step.startX
                ];
                step.yDimensions = [
                    step.endY,
                    step.startY
                ];
                const _endClosed = step.endClosed;
                step.endClosed = step.startClosed;
                step.startClosed = _endClosed;
            }
            if (params.flipVertically) {
                step.yDimensions = [
                    1 - step.startY,
                    1 - step.endY,
                ];
            }
            return step;
        });
        return pattern;
    }
    ;
    speed(pattern, params) {
        pattern.steps.map((step) => {
            step.xDimensions = [
                step.startX / params.scratchSpeed,
                step.endX / params.scratchSpeed,
            ];
            return step;
        });
        if (params.scratchSpeed > 1) {
            pattern = this.multiplyPatternToFill(pattern, params);
        }
        else {
            pattern.beats /= params.scratchSpeed;
        }
        return pattern;
    }
    ;
    multiplyPatternToFill(pattern, params) {
        const beatsNew = pattern.beats / params.scratchSpeed;
        for (let i = 1; i < params.scratchSpeed; i *= 2) {
            let stepsShifted = pattern.steps.map((step) => {
                let stepShifted = step.clone();
                stepShifted.xDimensions = [
                    stepShifted.startX + beatsNew * i,
                    stepShifted.endX + beatsNew * i,
                ];
                return stepShifted;
            });
            pattern.steps = pattern.steps.concat(stepsShifted);
        }
        return pattern;
    }
    phase(pattern, params) {
        if (params.phasing === 0) {
            return pattern;
        }
        params.phasing %= pattern.beats; // normalize overflow
        let stepsNew = [];
        pattern.steps.forEach((step) => {
            let startXNew = step.startX + params.phasing;
            let endXNew = step.endX + params.phasing;
            let isHandled = false;
            if (endXNew <= 0) {
                // move the whole step to end
                startXNew += pattern.beats;
                endXNew += pattern.beats;
                step.xDimensions = [startXNew, endXNew];
                stepsNew.push(step);
                isHandled = true;
            }
            else if (startXNew < 0) {
                // split the step and move first one to end
                const slope = (step.endY - step.startY) / (step.endX - step.startX);
                let intersectionYAxis = step.endY - slope * endXNew;
                let step1 = new Step([startXNew + pattern.beats, pattern.beats], [step.startY, intersectionYAxis], step.startClosed, false);
                let step2 = new Step([0, endXNew], [intersectionYAxis, step.endY], false, step.endClosed);
                //console.log('split steps', step1, step2);
                stepsNew.push(step1);
                stepsNew.push(step2);
                isHandled = true;
            }
            if (startXNew >= pattern.beats) {
                // move whole step to start
                startXNew -= pattern.beats;
                endXNew -= pattern.beats;
                step.xDimensions = [startXNew, endXNew];
                stepsNew.push(step);
                isHandled = true;
            }
            else if (endXNew > pattern.beats) {
                // split the step and move last one to start
                const slope = (step.endY - step.startY) / (step.endX - step.startX);
                let intersectionYAxis = step.endY - slope * endXNew;
                let calculateY = (x) => {
                    return slope * x + intersectionYAxis;
                };
                let step1 = new Step([startXNew, pattern.beats], [step.startY, calculateY(pattern.beats)], step.startClosed, false);
                let step2 = new Step([0, endXNew - pattern.beats], [calculateY(pattern.beats), step.endY], false, step.endClosed);
                //console.log('split steps', step1, step2);
                stepsNew.push(step1);
                stepsNew.push(step2);
                isHandled = true;
            }
            if (!isHandled) {
                step.xDimensions = [startXNew, endXNew];
                stepsNew.push(step);
            }
        });
        pattern.steps = stepsNew;
        return pattern;
    }
}
const patternTransformer = new PatternTransformer();
export default patternTransformer;
export { patternTransformer, PatternTransformer, PatternTransformParams, };
